/*
Season selector pill — renders 4 segmented buttons (Hiver / Printemps /
Été / Automne) and writes selections back to the SeasonStore.

Used in the Arboretum + Remarquables headers to switch between the live
season and archive modes. Stays in sync with the store via subscribe()
so two pills mounted on different screens stay aligned.
*/

import { getSeason, setSeason, subscribeSeason, kSeasonOrdinals, kSeasonNames, liveSeason } from '../../data/seasonStore'
import type { SeasonOrdinal } from '../../data/types'

/*
Build the pill markup as an HTML string. Caller is responsible for
inserting it into the screen and then calling `wireSeasonSelector(root)`
to attach handlers + sync to the store.
*/
export function renderSeasonSelector(): string
{
	const vCurrent = getSeason()  // active selection at render time
	const vLive = liveSeason()  // current real-world season for the dot indicator
	const vButtons = kSeasonOrdinals.map((inOrd) => {
		const vActive = inOrd === vCurrent ? 'true' : 'false'
		const vIsLive = inOrd === vLive ? 'true' : 'false'
		return `
			<button class="season-pill-btn" data-season="${inOrd}" data-active="${vActive}" data-live="${vIsLive}">
				${kSeasonNames[inOrd]}
			</button>
		`
	}).join('')
	return `<div class="season-pill" id="season-pill" role="tablist">${vButtons}</div>`
}

/*
Wire click handlers + subscribe to the store. Returns an unsubscribe
closure the caller invokes on screen unmount.
*/
export function wireSeasonSelector(inRoot: HTMLElement, inOnChange: (inSeason: SeasonOrdinal) => void): () => void
{
	const vPill = inRoot.querySelector<HTMLElement>('#season-pill')
	if (!vPill) return () => { /* nothing to unsubscribe */ }

	vPill.addEventListener('click', (inEvent) => {
		const vTarget = (inEvent.target as HTMLElement).closest<HTMLElement>('[data-season]')
		if (!vTarget) return
		const vOrd = Number(vTarget.dataset.season) as SeasonOrdinal
		if (Number.isFinite(vOrd)) setSeason(vOrd)
	})

	return subscribeSeason((inSeason) => {
		// Repaint the active state without rebuilding the pill so any
		// in-progress focus / aria state stays intact.
		vPill.querySelectorAll<HTMLElement>('[data-season]').forEach((inBtn) => {
			const vOrd = Number(inBtn.dataset.season)
			inBtn.dataset.active = vOrd === inSeason ? 'true' : 'false'
		})
		inOnChange(inSeason)
	})
}

/*
Markup for the archive banner shown above the list when the selected
season ≠ the live season. Read-only mode is just a visual cue; data
loaders apply the season filter regardless.
*/
export function renderArchiveBanner(inSeason: SeasonOrdinal): string | null
{
	if (inSeason === liveSeason()) return null
	return `
		<div class="archive-banner" role="status">
			📆 Archive ${kSeasonNames[inSeason]} · lecture seule
		</div>
	`
}
